import { useEffect, useMemo, useState } from "react";
import {
  listarSesiones,
  listarPacientes,
  crearSesion,
  obtenerPaciente,
  listarEspecialidades,
} from "../api";
import "./AgendarSesion.css";

/** Normaliza para buscar: minúsculas + sin tildes + sin dobles espacios */
const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Extrae especialidades del paciente desde distintos formatos posibles.
 * Devuelve [{code, nombre}]
 *
 * ✅ soporta specialty_ids mapeando con catálogo allSpecs
 * ✅ fallback a legacy patient.servicios
 */
function extractPatientSpecialties(patient, allSpecs) {
  // 1) Formato ideal: patient.specialties = [{id, code, nombre}]
  if (Array.isArray(patient?.specialties) && patient.specialties.length) {
    const arr = patient.specialties;

    if (typeof arr[0] === "object" && arr[0] !== null) {
      const hasFlag = [
        "selected",
        "checked",
        "asignada",
        "assigned",
        "is_selected",
        "isChecked",
      ].some((k) => k in (arr[0] || {}));

      const filtered = hasFlag
        ? arr.filter(
            (s) =>
              s.selected ||
              s.checked ||
              s.asignada ||
              s.assigned ||
              s.is_selected ||
              s.isChecked
          )
        : arr;

      return filtered
        .map((s) => ({
          code: String(s.code || "").trim(),
          nombre: String(s.nombre || s.name || s.label || s.code || "").trim(),
        }))
        .filter((s) => s.code);
    }

    // Si vienen strings ["logopeda", "psicologia"]
    return arr
      .map((code) => ({
        code: String(code || "").trim(),
        nombre: String(code || "").trim(),
      }))
      .filter((s) => s.code);
  }

  // 2) Alternativas comunes en arrays
  const candidates = [
    patient?.especialidades,
    patient?.especialidades_asignadas,
    patient?.specialty_codes,
    patient?.specialties_codes,
    patient?.specialtyCodes,
    patient?.patient_specialties,
  ].find((x) => Array.isArray(x) && x.length);

  if (Array.isArray(candidates) && candidates.length) {
    if (typeof candidates[0] === "object" && candidates[0] !== null) {
      return candidates
        .map((it) => {
          const obj = it.specialty || it;
          const code = String(obj.code || "").trim();
          const nombre = String(
            obj.nombre || obj.name || obj.label || obj.code || ""
          ).trim();
          return { code, nombre };
        })
        .filter((s) => s.code);
    }

    return candidates
      .map((code) => ({
        code: String(code || "").trim(),
        nombre: String(code || "").trim(),
      }))
      .filter((s) => s.code);
  }

  // 3) patient.specialty_ids = [1,2] -> mapear usando catálogo allSpecs
  if (
    Array.isArray(patient?.specialty_ids) &&
    patient.specialty_ids.length &&
    Array.isArray(allSpecs) &&
    allSpecs.length
  ) {
    const out = patient.specialty_ids
      .map((id) => {
        const sp = allSpecs.find((s) => Number(s.id) === Number(id));
        if (!sp) return null;
        return {
          code: String(sp.code).trim(),
          nombre: String(sp.nombre || sp.code).trim(),
        };
      })
      .filter(Boolean);
    if (out.length) return out;
  }

  // 4) Fallback legacy: patient.servicios (string o array)
  const legacyServicios = Array.isArray(patient?.servicios)
    ? patient.servicios
    : String(patient?.servicios || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  if (legacyServicios.length) {
    if (Array.isArray(allSpecs) && allSpecs.length) {
      const out = legacyServicios
        .map((code) => {
          const sp = allSpecs.find((s) => s.code === code);
          return sp
            ? { code: sp.code, nombre: sp.nombre || sp.code }
            : { code, nombre: code };
        })
        .filter((x) => x.code);
      if (out.length) return out;
    }
    return legacyServicios.map((code) => ({ code, nombre: code }));
  }

  // 5) Último fallback: patient.specialty (string única)
  if (patient?.specialty) {
    const code = String(patient.specialty).trim();
    return code ? [{ code, nombre: code }] : [];
  }

  return [];
}

export default function AgendarSesion({ preselectedPatientId }) {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(preselectedPatientId || "");

  const [service, setService] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [isGroup, setIsGroup] = useState(false);
  const [groupSize, setGroupSize] = useState(2);

  const [msg, setMsg] = useState("");
  const [q, setQ] = useState("");

  const [patientSpec, setPatientSpec] = useState("");
  const [availableSpecs, setAvailableSpecs] = useState([]); // [{code,nombre}]

  // catálogo de especialidades para mapear ids/codes
  const [allSpecs, setAllSpecs] = useState([]); // [{id,code,nombre}]

  // cargar catálogo de especialidades
  useEffect(() => {
    (async () => {
      try {
        const r = await listarEspecialidades();
        const items = Array.isArray(r)
          ? r
          : Array.isArray(r?.items)
          ? r.items
          : [];
        setAllSpecs(items);
      } catch (e) {
        console.error("Error cargando catálogo de especialidades", e);
        setAllSpecs([]);
      }
    })();
  }, []);

  // cargar TODOS los pacientes
  useEffect(() => {
    (async () => {
      try {
        const all = [];
        let page = 1;
        let pages = 1;

        do {
          const res = await listarPacientes({ page, page_size: 200 });
          if (res?.items?.length) all.push(...res.items);
          pages = Number(res?.pages || 1);
          page += 1;
        } while (page <= pages);

        setPatients(all);
      } catch (e) {
        console.error("Error cargando pacientes", e);
      }
    })();
  }, []);

  // cuando seleccionamos paciente, traer especialidades (con fallback)
  useEffect(() => {
    (async () => {
      if (!patientId) {
        setAvailableSpecs([]);
        setPatientSpec("");
        setService("");
        setSelectedSlot(null);
        return;
      }

      try {
        const patient = await obtenerPaciente(patientId);
        const specs = extractPatientSpecialties(patient, allSpecs);

        setAvailableSpecs(specs);
        setPatientSpec(specs?.[0]?.code || "");

        setService("");
        setSelectedSlot(null);
      } catch (e) {
        console.error("Error cargando paciente", e);
        setAvailableSpecs([]);
        setPatientSpec("");
        setService("");
        setSelectedSlot(null);
      }
    })();
  }, [patientId, allSpecs]);

  // servicios por especialidad (hardcodeado)
  const servicesBySpec = {
    psicologia: [
      { code: "consulta_inicial", label: "Consulta Inicial (20m)", duration: 20 },
      { code: "psicoterapia", label: "Psicoterapia (45m)", duration: 45 },
      { code: "estimulacion", label: "Estimulacion (45m)", duration: 45 },
      { code: "terapia_familiar", label: "Terapia familiar (60m)", duration: 60 },
      { code: "psicologia_perinatal", label: "Psicología perinatal (50m)", duration: 50 },
      { code: "asesoramiento_crianza", label: "Asesoramiento crianza (50m)", duration: 50 },
      { code: "atencion_temprana", label: "Atención temprana (45m)", duration: 45 },
      { code: "reeducacion_psicopedagogica", label: "Reeducacion psicopedagogica (45m)", duration: 45 },
      { code: "entrevista_inicial", label: "Entrevista inicial (45m)", duration: 45 },
      { code: "evaluacion_psicologica", label: "Evaluación psicológica (60m)", duration: 60 },
      { code: "intervencion_psicoterapia_mes", label: "Intervencion Psicoterapia mensual(45m)", duration: 45 },
      { code: "intervencion_estimulacion_mes", label: "Intervencion Estimulacion mensual(45m)", duration: 45 },
      { code: "reeducacion_pedagogica_NEAE_mes", label: "Reeducacion pedagogica NEAE mes(45m)", duration: 45 },
      { code: "altas_capacidades_NEAE_mes", label: "Altas capaciades NEAE mes(45m)", duration: 45 },
    ],
    "psicologia-m": [
      { code: "consulta_inicial", label: "Consulta Inicial (20m)", duration: 20 },
      { code: "evaluacion_psicologica", label: "Evaluación psicológica (60m)", duration: 60 },
      { code: "consulta_inicial", label: "Consulta Inicial (20m)", duration: 20 },
      { code: "psicoterapia", label: "Psicoterapia (45m)", duration: 45 },
      { code: "estimulacion", label: "Estimulacion (45m)", duration: 45 },
      { code: "terapia_familiar", label: "Terapia familiar (60m)", duration: 60 },
      { code: "psicologia_perinatal", label: "Psicología perinatal (50m)", duration: 50 },
      { code: "asesoramiento_crianza", label: "Asesoramiento crianza (50m)", duration: 50 },
      { code: "atencion_temprana", label: "Atención temprana (45m)", duration: 45 },
      { code: "reeducacion_psicopedagogica", label: "Reeducacion psicopedagogica (45m)", duration: 45 },
      { code: "entrevista_inicial", label: "Entrevista inicial (45m)", duration: 45 },
      { code: "evaluacion_psicologica", label: "Evaluación psicológica (60m)", duration: 60 },
      { code: "intervencion_psicoterapia_mes", label: "Intervencion Psicoterapia mensual(45m)", duration: 45 },
      { code: "intervencion_estimulacion_mes", label: "Intervencion Estimulacion mensual(45m)", duration: 45 },
      { code: "reeducacion_pedagogica_NEAE_mes", label: "Reeducacion pedagogica NEAE mes(45m)", duration: 45 },
      { code: "altas_capacidades_NEAE_mes", label: "Altas capaciades NEAE mes(45m)", duration: 45 },
    ],
    "psicologia-g": [
      { code: "consulta_inicial", label: "Consulta Inicial (20m)", duration: 20 },
      { code: "evaluacion_psicologica", label: "Evaluación psicológica (60m)", duration: 60 },
      { code: "consulta_inicial", label: "Consulta Inicial (20m)", duration: 20 },
      { code: "psicoterapia", label: "Psicoterapia (45m)", duration: 45 },
      { code: "estimulacion", label: "Estimulacion (45m)", duration: 45 },
      { code: "terapia_familiar", label: "Terapia familiar (60m)", duration: 60 },
      { code: "psicologia_perinatal", label: "Psicología perinatal (50m)", duration: 50 },
      { code: "asesoramiento_crianza", label: "Asesoramiento crianza (50m)", duration: 50 },
      { code: "atencion_temprana", label: "Atención temprana (45m)", duration: 45 },
      { code: "reeducacion_psicopedagogica", label: "Reeducacion psicopedagogica (45m)", duration: 45 },
      { code: "entrevista_inicial", label: "Entrevista inicial (45m)", duration: 45 },
      { code: "evaluacion_psicologica", label: "Evaluación psicológica (60m)", duration: 60 },
      { code: "intervencion_psicoterapia_mes", label: "Intervencion Psicoterapia mensual(45m)", duration: 45 },
      { code: "intervencion_estimulacion_mes", label: "Intervencion Estimulacion mensual(45m)", duration: 45 },
      { code: "reeducacion_pedagogica_NEAE_mes", label: "Reeducacion pedagogica NEAE mes(45m)", duration: 45 },
      { code: "altas_capacidades_NEAE_mes", label: "Altas capaciades NEAE mes(45m)", duration: 45 },
    ],
    logopeda: [
      { code: "consulta_inicial", label: "Consulta Inicial (20m)", duration: 20 },
      { code: "entrevista_inicial", label: "Entrevista inicial (45m)", duration: 45 },
      { code: "logopedia", label: "Logopedia (45m)", duration: 45 },
      { code: "deglucion", label: "Deglucion (30m)", duration: 30 },
      { code: "evaluacion_logopedia", label: "Evaluación logopedia (60m)", duration: 60 },
      { code: "intervencion_logopedia_mes", label: "Intervencion Logopedia (45m)", duration: 45 },
      { code: "reeducacion_lenguaje_NEAE_mes", label: "Reeducacion del lenguaje NEAE mes(45m)", duration: 45 },
    ],
    yoga: [
      { code: "clase_yoga", label: "Clase de yoga (45m)", duration: 45 },
      { code: "clase_yoga_mes", label: "Mes yoga (45m)", duration: 45 },
    ],
    nutricion: [
      { code: "consulta_inicial", label: "Consulta Inicial (20m)", duration: 20 },
      { code: "terapia_familiar", label: "Terapia familiar (60m)", duration: 60 },
      { code: "consulta_nutricional", label: "Consulta nutricional (45m)", duration: 45 },
    ],
  };

  /**
   * ✅ Slots:
   * - Genera posibles inicios con una rejilla (step) para que aparezcan huecos reales (ej. 19:15).
   * - El fin siempre es: inicio + duración del servicio seleccionado.
   * - Sin pausa para psicología.
   * - Las citas ya existentes NO se tocan (solo cambia qué botones se muestran).
   */
  useEffect(() => {
    const serviceDef = servicesBySpec[patientSpec]?.find((s) => s.code === service);
    if (!date || !serviceDef) {
      setSlots([]);
      return;
    }

    const duration = serviceDef.duration;

    // ✅ step inteligente: si duración es múltiplo de 15 -> 15; si no -> 5 (para 20/50)
    const stepMinutes = duration % 15 === 0 ? 15 : 5;

    const generateSlots = (startHHMM, endHHMM, stepMins) => {
      const out = [];
      let start = new Date(`${date}T${startHHMM}:00`);
      const end = new Date(`${date}T${endHHMM}:00`);

      const stepMs = stepMins * 60000;
      const durMs = duration * 60000;

      while (start.getTime() + durMs <= end.getTime()) {
        out.push({
          start: new Date(start),
          end: new Date(start.getTime() + durMs),
        });
        start = new Date(start.getTime() + stepMs);
      }
      return out;
    };

    const OPEN = "10:00";
    const CLOSE = "21:00";

    let newSlots = generateSlots(OPEN, CLOSE, stepMinutes);

    listarSesiones({ specialty: patientSpec, date })
      .then((r) => {
        const occupied = r.items || [];

        newSlots = newSlots.map((slot) => {
          const clashes = occupied.filter(
            (occ) =>
              slot.start < new Date(occ.end_datetime) &&
              slot.end > new Date(occ.start_datetime)
          );

          const hasSimple = clashes.some((occ) => !occ.is_group);
          const hasGroup = clashes.some((occ) => occ.is_group);

          let occupiedSlot = false;
          if (hasSimple) occupiedSlot = true;
          else if (hasGroup) occupiedSlot = !isGroup;

          return { ...slot, occupied: occupiedSlot };
        });

        setSlots(newSlots);
      })
      .catch((e) => {
        console.error(e);
        setSlots(newSlots);
      });
  }, [service, date, patientSpec, isGroup]);

  const formatLocalDateTime = (d) => {
    const pad = (n) => (n < 10 ? "0" + n : n);
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      " " +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes()) +
      ":" +
      pad(d.getSeconds())
    );
  };

  const submit = async () => {
    try {
      setMsg("");
      if (!patientId || !patientSpec || !service || !selectedSlot) {
        setMsg("Faltan datos");
        return;
      }

      const r = await listarSesiones({ specialty: patientSpec, date });
      const occupied = r.items || [];

      const clashes = occupied.filter(
        (occ) =>
          selectedSlot.start < new Date(occ.end_datetime) &&
          selectedSlot.end > new Date(occ.start_datetime)
      );

      const hasSimple = clashes.some((occ) => !occ.is_group);
      if (hasSimple && !isGroup) {
        setMsg("Ese horario ya está ocupado ❌");
        return;
      }

      const serviceDef = servicesBySpec[patientSpec]?.find((s) => s.code === service);
      if (!serviceDef) {
        setMsg("Servicio inválido para esa especialidad");
        return;
      }

      await crearSesion({
        patient_id: Number(patientId),
        specialty: patientSpec,
        service_code: service,
        start_datetime: formatLocalDateTime(selectedSlot.start),
        end_datetime: formatLocalDateTime(selectedSlot.end),
        duration_minutes: serviceDef.duration,
        is_group: isGroup ? 1 : 0,
        group_size: isGroup ? Number(groupSize) : null,
      });

      setMsg("Sesión agendada ✅");
    } catch (e) {
      setMsg("Error: " + (e.message || "Error desconocido"));
    }
  };

  // buscador por tokens, sin tildes
  const filteredPatients = useMemo(() => {
    const nq = normalize(q);
    if (!nq) return patients;
    const tokens = nq.split(" ").filter(Boolean);
    return patients.filter((p) => {
      const name = normalize(p.nombre);
      return tokens.every((t) => name.includes(t));
    });
  }, [patients, q]);

  return (
    <div className="agenda-sesion">
      <h2>Agendar Sesión</h2>

      <div className="form-row">
        <label>Paciente</label>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar paciente..."
        />
        <select value={patientId} onChange={(e) => setPatientId(e.target.value)}>
          <option value="">-- Seleccionar --</option>
          {filteredPatients.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </select>
      </div>

      {patientId && (
        <>
          <div className="form-row">
            <label>Especialidad</label>

            {availableSpecs.length > 1 ? (
              <select
                value={patientSpec}
                onChange={(e) => {
                  setPatientSpec(e.target.value);
                  setService("");
                  setSelectedSlot(null);
                }}
              >
                {availableSpecs.map((sp) => (
                  <option key={sp.code} value={sp.code}>
                    {sp.nombre || sp.code}
                  </option>
                ))}
              </select>
            ) : availableSpecs.length === 1 ? (
              <input
                type="text"
                value={availableSpecs[0].nombre || availableSpecs[0].code}
                disabled
              />
            ) : (
              <p style={{ margin: 0 }}>
                Este paciente no tiene especialidades asignadas ❌
              </p>
            )}
          </div>

          {patientSpec && (
            <>
              <div className="form-row">
                <label>Servicio</label>
                <select value={service} onChange={(e) => setService(e.target.value)}>
                  <option value="">-- Seleccionar --</option>
                  {servicesBySpec[patientSpec]?.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label>Fecha</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>

              <div className="slots-panel">
                {slots.length === 0 && <p>Selecciona fecha y servicio</p>}
                {slots.map((slot, idx) => (
                  <button
                    key={idx}
                    className={`slot-bubble ${slot.occupied ? "occupied" : ""} ${
                      selectedSlot === slot ? "selected" : ""
                    }`}
                    disabled={slot.occupied && !isGroup}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    {slot.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} -{" "}
                    {slot.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </button>
                ))}
              </div>

              <div className="form-row">
                <label>
                  <input
                    type="checkbox"
                    checked={isGroup}
                    onChange={(e) => setIsGroup(e.target.checked)}
                  />
                  Sesión múltiple
                </label>
                {isGroup && (
                  <input
                    type="number"
                    min="2"
                    max="5"
                    value={groupSize}
                    onChange={(e) => setGroupSize(e.target.value)}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      <button onClick={submit}>Agendar</button>
      {msg && <p>{msg}</p>}
    </div>
  );
}